import {
  useMemo,
  useState,
} from 'react';
import type {
  SubmitEvent,
  ChangeEvent,
} from 'react';
import classNames from 'classnames';
import {
  useAlerts,
  useAuthentication,
} from '@dinosave/core/stores';
import type {
  UserEntity,
} from '@dinosave/core/types';
import {
  parseError,
  passwordStrength,
} from '@dinosave/core/utils';
import {
  validateEmail,
  validatePassword,
} from '@dinosave/core/validations';
import Button from '@/ui/components/Button';
import Input from '@/ui/components/Input';
import Text from '@/ui/components/Text';
import {
  EyeIcon,
  EyeOffIcon,
  InformationIcon,
} from '@/ui/icons';

import styles from './SignUp.module.css';

type AuthenticatedResponse = {
  user: UserEntity;
  token: string;
}

type SignUpProps = {
  kind?: 'default' | 'flat';
  onSuccess?: (response: AuthenticatedResponse) => void;
  navigateToSignIn: () => void;
}

const SignUp = (props: SignUpProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    kind = 'default',
    onSuccess,
    navigateToSignIn,
  } = props;

  // ##########################################################################
  // STORES
  // ##########################################################################

  const createAuthenticatedUser = useAuthentication(s => s.createAuthenticatedUser);

  const addError = useAlerts(s => s.addError);

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nameInvalid, setNameInvalid] = useState(false);
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);
  const [passwordInvalid, setPasswordInvalid] = useState(false);
  const [loading, setLoading] = useState(false);

  // ##########################################################################
  // MEMOIZATION
  // ##########################################################################

  const isNameValid = useMemo(() => (
    !!name.length
  ), [name]);

  const isEmailValid = useMemo(() => (
    validateEmail(email)
  ), [email]);

  const isPasswordValid = useMemo(() => (
    validatePassword(password)
  ), [password]);

  const passwordStrengthLevel = useMemo(() => (
    passwordStrength(password)
  ), [password]);

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const togglePassword = () => setPasswordVisible(!passwordVisible);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNameInvalid(false);
    setName(e.target.value);
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmailInvalid(false);
    setEmailTaken(false);
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPasswordInvalid(false);
    setPassword(e.target.value);
  };

  const handleSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      loading ||
      emailTaken
    ) return;

    if (
      !isNameValid
    ) setNameInvalid(true);
    if (
      !isEmailValid
    ) setEmailInvalid(true);
    if (
      !isPasswordValid
    ) setPasswordInvalid(true);

    const isAllValid = isEmailValid && isNameValid && isPasswordValid;

    if (
      isAllValid
    ) {
      setLoading(true);

      try {
        const user = {
          name,
          email,
          password,
        } as UserEntity;
        const authResponse = await createAuthenticatedUser(user);

        if (
          onSuccess
        ) onSuccess(authResponse);

      } catch (e) {
        setLoading(false);
        const error = parseError(e);

        if (
          error?.status === 409
        ) return setEmailTaken(true);

        addError(e);
      }
    }
  };

  // ##########################################################################
  // COMPUTED STYLES
  // #########################################################################

  const signUpClassName = classNames({
    [styles.signUp]: true,
    [styles.flat]: kind === 'flat',
  });

  const strengthProgressClassName = classNames({
    [styles.strengthProgress]: true,
    [styles[passwordStrengthLevel]]: true,
  });

  const strengthClassName = classNames({
    [styles.strength]: true,
    [styles[passwordStrengthLevel]]: true,
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <form
      className={signUpClassName}
      onSubmit={handleSubmit}
    >
      <div className={styles.fields}>
        <Text kind="label">
          NAME
        </Text>

        <div className={styles.input}>
          <Input
            value={name}
            onChange={handleNameChange}
            placeholder="Enter your name"
            invalid={nameInvalid}
            autoFocus
          />
        </div>

        <Text kind="label">
          EMAIL
        </Text>

        <div className={styles.input}>
          <Input
            value={email}
            onChange={handleEmailChange}
            placeholder="Enter your Email"
            invalid={emailInvalid}
          />
        </div>

        {emailTaken && (
          <div className={styles.emailTaken}>
            <InformationIcon color="var(--danger)" />

            <div className={styles.mobile}>
              <Text
                kind="caption"
                size="regular"
                color="danger"
                weight="500"
              >
                Email is already taken.
              </Text>
            </div>

            <div className={styles.desktop}>
              <Text
                kind="caption"
                size="regular"
                color="danger"
                weight="500"
              >
                This email is already in use.
              </Text>
            </div>

            <div className={styles.link}>
              <Button
                kind="link"
                onClick={navigateToSignIn}
              >
                <Text
                  kind="caption"
                  size="regular"
                  color="primary-600"
                  weight="500"
                >
                  Sign In?
                </Text>
              </Button>
            </div>
          </div>
        )}

        <div className={styles.password}>
          <Text kind="label">
            PASSWORD
          </Text>

          {isPasswordValid ? (
            <div className={strengthClassName}>
              <Text
                kind="caption"
                size="small"
                color="neutral-500"
              >
                Your password is&nbsp;
              </Text>

              <div className={styles.strengthValue}>
                <Text
                  kind="caption"
                  size="small"
                  weight="500"
                >
                  {passwordStrengthLevel}
                </Text>
              </div>
            </div>
          ) : (
            <div className={styles.requirement}>
              <div className={styles.mobile}>
                <Text
                  kind="caption"
                  size="small"
                  color="neutral-500"
                >
                  Min 6 characters
                </Text>
              </div>

              <div className={styles.desktop}>
                <Text
                  kind="caption"
                  size="small"
                  color="neutral-500"
                >
                  Minimum 6 characters
                </Text>
              </div>
            </div>
          )}

          <div className={styles.input}>
            <Input
              value={password}
              onChange={handlePasswordChange}
              placeholder="Enter your password"
              type={passwordVisible ? 'text' : 'password'}
              invalid={passwordInvalid}
            />
          </div>

          <div className={styles.passwordReveal}>
            <Button
              kind="ghost"
              color="secondary"
              onClick={togglePassword}
              centered
            >
              {passwordVisible ? <EyeOffIcon /> : <EyeIcon />}
            </Button>
          </div>

          <div className={strengthProgressClassName}>
            <div className={styles.strengthProgressLine} />
            <div className={styles.strengthProgressLine} />
            <div className={styles.strengthProgressLine} />
          </div>
        </div>

        <div className={styles.disclaimer}>
          <Text
            kind="caption"
            size="small"
            color="neutral-500"
          >
            By pressing “Create account” you acknowledge that you’ve read and agreed to the&nbsp;
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>
            &nbsp;and&nbsp;
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </Text>
        </div>

        <div className={styles.submit}>
          <Button
            color="primary"
            roundness="round"
            disabled={loading}
            loading={loading}
            type="submit"
            centered
          >
            <Text
              kind="control"
              size="regular"
            >
              {loading ? 'Just a moment...' : 'Create Account'}
            </Text>
          </Button>
        </div>
      </div>
    </form>
  );
};

export default SignUp;
